import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getGetNotificationsQueryKey } from '../client_gen_gen_generated/notifications';
import {
  resolveNotificationApiBaseUrl,
  resolveNotificationStreamPath,
  resolveNotificationApiKey,
} from '../custom-axios';

export interface UseNotificationSSEOptions {
  recipientId: string | null;
  profileId?: number | null;
  enabled?: boolean;
  onNotification?: () => void;
  onUnreadCountChange?: (count: number) => void;
  onError?: (error: Event) => void;
  reconnectDelay?: number;
  baseUrl?: string;
}

export function useNotificationSSE({
  recipientId,
  profileId,
  enabled = true,
  onNotification,
  onUnreadCountChange,
  onError,
  reconnectDelay = 5000,
  baseUrl,
}: UseNotificationSSEOptions) {
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectedRef = useRef(false);

  const resolvedBaseUrl = useMemo(() => baseUrl ?? resolveNotificationApiBaseUrl(), [baseUrl]);
  const streamPath = useMemo(() => resolveNotificationStreamPath(), []);
  const notificationApiKey = useMemo(() => resolveNotificationApiKey(), []);

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      isConnectedRef.current = false;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const buildStreamUrl = useCallback(
    (id: string | number) => {
      try {
        const target = streamPath.startsWith('http')
          ? new URL(streamPath)
          : new URL(streamPath, resolvedBaseUrl);

        target.searchParams.set('recipientId', String(id));

        if (notificationApiKey && !target.searchParams.has('apiKey')) {
          target.searchParams.set('apiKey', notificationApiKey);
        }

        return target.toString();
      } catch (error) {
        console.error('[notification-client] Failed to compose SSE URL', {
          baseUrl: resolvedBaseUrl,
          streamPath,
          id,
          error,
        });

        const sanitizedBase = resolvedBaseUrl.endsWith('/')
          ? resolvedBaseUrl.slice(0, -1)
          : resolvedBaseUrl;
        const normalizedPath = streamPath.startsWith('/') ? streamPath : `/${streamPath}`;
        const fallbackUrl = `${sanitizedBase}${normalizedPath}?recipientId=${encodeURIComponent(String(id))}`;
        if (notificationApiKey) {
          return `${fallbackUrl}&apiKey=${encodeURIComponent(notificationApiKey)}`;
        }
        return fallbackUrl;
      }
    },
    [resolvedBaseUrl, streamPath, notificationApiKey]
  );

  const invalidateNotifications = useCallback(() => {
    if (recipientId) {
      queryClient.invalidateQueries({
        queryKey: getGetNotificationsQueryKey({
          recipientId,
          limit: 50,
        }),
      });
    }
  }, [queryClient, recipientId]);

  const connect = useCallback(() => {
    if (!enabled || !recipientId) {
      return;
    }

    const streamId = profileId ?? recipientId;
    const streamUrl = buildStreamUrl(streamId);

    try {
      const source = new EventSource(streamUrl, { withCredentials: true });
      eventSourceRef.current = source;

      source.addEventListener('notification', () => {
        invalidateNotifications();
        onNotification?.();
      });

      source.addEventListener('unread-count', (event) => {
        try {
          const payload = JSON.parse((event as MessageEvent).data) as { count?: number };
          if (typeof payload.count === 'number') {
            onUnreadCountChange?.(payload.count);
          }
        } catch (error) {
          console.warn('[notification-client] Failed to parse unread-count event', error);
        }
      });

      source.addEventListener('open', () => {
        isConnectedRef.current = true;
        console.log('[notification-client] SSE connected');
      });

      source.addEventListener('error', (event) => {
        console.warn('[notification-client] SSE error', event);
        isConnectedRef.current = false;
        onError?.(event);

        source.close();
        if (!reconnectTimerRef.current && enabled) {
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
          }, reconnectDelay);
        }
      });
    } catch (error) {
      console.error('[notification-client] Failed to create EventSource', error);
    }
  }, [
    enabled,
    recipientId,
    profileId,
    buildStreamUrl,
    invalidateNotifications,
    onNotification,
    onUnreadCountChange,
    onError,
    reconnectDelay,
  ]);

  const reconnect = useCallback(() => {
    cleanup();
    connect();
  }, [cleanup, connect]);

  useEffect(() => {
    if (enabled && recipientId) {
      connect();
    }

    return cleanup;
  }, [enabled, recipientId, connect, cleanup]);

  return {
    isConnected: isConnectedRef.current,
    reconnect,
    disconnect: cleanup,
  };
}
